import type { Position, Player } from "../../../shared/models/Player";
import type { TiersByPos } from "../../../shared/utils/xlsxRankings";

export type ImportedCompareList = {
  id: string;
  title: string;
  rankingIds: string[];
};

export function normalizeImportedTierBreaks(
  pos: Position,
  breaks: string[],
  rankingOrder: string[],
  players: Player[]
): string[] {
  const playersByIdLocal = Object.fromEntries(players.map((player) => [player.id, player]));
  const posIds = rankingOrder.filter((id) => playersByIdLocal[id]?.position === pos);
  const posSet = new Set(posIds);
  const unique: string[] = [];
  const seen = new Set<string>();

  for (const id of breaks) {
    if (!posSet.has(id)) continue;
    if (posIds.indexOf(id) <= 0) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    unique.push(id);
  }

  unique.sort((a, b) => posIds.indexOf(a) - posIds.indexOf(b));
  return unique;
}

export function filterRankingIdsByVisibility(
  rankingIds: string[],
  playersById: Record<string, Player>,
  hideKickers: boolean,
  hideDefenses: boolean
) {
  return rankingIds.filter((id) => {
    const player = playersById[id];
    if (!player) return false;
    if (hideKickers && player.position === "K") return false;
    if (hideDefenses && player.position === "DST") return false;
    return true;
  });
}

export function cloneVisibleTiersForRankingIds(
  sourceIds: string[],
  sourceTiers: TiersByPos,
  playersById: Record<string, Player>
): TiersByPos {
  const sourceSet = new Set(sourceIds);
  const normalizeForPos = (pos: Position, breaks: string[]) => {
    const posIds = sourceIds.filter((id) => playersById[id]?.position === pos);
    const posSet = new Set(posIds);
    const next: string[] = [];
    const seen = new Set<string>();
    for (const id of breaks) {
      if (!sourceSet.has(id)) continue;
      if (!posSet.has(id)) continue;
      if (posIds.indexOf(id) <= 0) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      next.push(id);
    }
    next.sort((a, b) => posIds.indexOf(a) - posIds.indexOf(b));
    return next;
  };

  return {
    QB: normalizeForPos("QB", sourceTiers.QB ?? []),
    RB: normalizeForPos("RB", sourceTiers.RB ?? []),
    WR: normalizeForPos("WR", sourceTiers.WR ?? []),
    TE: normalizeForPos("TE", sourceTiers.TE ?? []),
    K: normalizeForPos("K", sourceTiers.K ?? []),
    DST: normalizeForPos("DST", sourceTiers.DST ?? []),
  };
}
